<template>
  <div>
    <v-card-title>
      <h2>Organization Management</h2>
      <v-spacer/>
      <v-btn
        dark
        color="red"
        round
        @click="newOrg = true;editOrg = {};"
      >
        <v-icon dark>add</v-icon>
        New Organization
      </v-btn>
    </v-card-title>
    <br />
    <v-layout
      row wrap
    >
      <v-flex
        sm12
        md3
        style="padding:0 5px 0 5px;"
      >
        <v-card>
          <v-toolbar
            dense
            flat
            color="primary"
            dark
          >
            Search &amp; Filtering
          </v-toolbar>
          <v-card-text>
            <v-text-field
              v-model="searchText"
              append-icon="search"
              label="Search Text"
              single-line
              hide-details
              @blur="search"
              v-on:keyup.enter="search"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer/>
            <v-btn
              @click="searchText='';search()"
            >
              Clear
            </v-btn>
            <v-btn
              color="primary"
              @click="search"
            >
              Search
            </v-btn>
            <v-spacer/>
          </v-card-actions>
        </v-card>
      </v-flex>
      <v-flex
        sm12
        md9
      >
        <v-card>
          <v-data-table
            :items="orgs"
            :headers="headers"
            :pagination.sync="pagination"
            :total-items="totalUsers"
            :rows-per-page-items="paginationStrings"
            :loading="loading"
            disable-initial-sort
          >
            <template slot="no-data">
              <tr>
                <td colspan="5">
                  <div style="text-align:center;padding:25px;">
                    <v-icon size="125px">find_in_page</v-icon>
                    <h3>No Results</h3>
                  </div>
                </td>
              </tr>
            </template>
            <template
              slot="items"
              slot-scope="props"
            >
              <tr
                :style="props.item.enabled ? '' : 'background:rgba(242, 38, 19, .3)'"
              >
                <td>
                  <img
                    :src="'data:image/png;base64,' + props.item.curr_icon"
                    style="max-width:35px;padding-top:5px;"
                  >
                </td>
                <td>
                  {{ props.item.name }}
                </td>
                <td>
                  {{ props.item.subdomain }}
                </td>
                <td class="justify-center layout px-0">
                  <v-icon
                    small
                    class="mr-2"
                    @click="editOrg = props.item;editing = true"
                  >
                    edit
                  </v-icon>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>
    <v-dialog
      v-model="editing"
      transition="dialog-bottom-transition"
      scrollable
      fullscreen
    >
      <v-card
        color="white"
      >
        <v-toolbar
          color="primary"
          flat
          dark
        >
          <v-icon
            left
          >
            domain
          </v-icon>
          Edit Organization
          <v-spacer />
          <v-btn
            fab
            flat
            dark
            @click="editing = false; failedIcon = false; failedLogo = false"
          >
            x
          </v-btn>
        </v-toolbar>
        <v-card-text
          v-if="editing"
        >
          <div style="height:100px;width:100%;padding:20px;">
            <img
              :src="'data:image/png;base64,' + editOrg.curr_logo"
              alt="Organization Logo"
              style="max-width: 30%;float:left;padding-right:20px;max-height:60px;"
            />
            <span>{{editOrg.name}}</span>
            <br />
            <span>{{editOrg.subdomain}}.{{getRootDomain()}}</span>
          </div>
          <v-tabs
            flat
            slider-color="primary"
          >
            <v-tab
              key="users"
            >
              <v-icon left>
                group
              </v-icon>
              Users
            </v-tab>
            <v-tab
              key="settings"
            >
              <v-icon left>
                settings
              </v-icon>
              Settings
            </v-tab>
            <v-tab-item
              key="users"
            >
              <SuperUserUsers
                v-if="editing"
                :organization="editOrg.id"
              />
            </v-tab-item>
            <v-tab-item
              key="settings"
              style="padding:25px;"
              v-if="editing"
            >
              <v-switch
                v-model="editOrg.enabled"
              >
                <template v-slot:label>
                  <span style="color: black">Enable</span>
                </template>
              </v-switch>
              <v-form 
                v-model="isOrgSettingValid"
              >
                <v-container fluid>
                  <v-text-field
                    v-model="editOrg.name"
                    label="Organization Name"
                    browser-autocomplete="off"
                    :rules="rules.orgName"
                  />
                  <v-text-field
                    v-model="editOrg.subdomain"
                    label="Organization Subdomain"
                    browser-autocomplete="off"
                    :suffix="'.' + getRootDomain()"
                    :rules="rules.orgName"
                  />
                
                  <v-layout row wrap>
                    <v-flex xs6>
                      <v-text-field
                        v-model="editOrg.support_email"
                        label="Support Email"
                        :rules="rules.email"
                        browser-autocomplete="off"
                      />
                    </v-flex>
                    <v-flex xs6>
                      <v-text-field
                        v-model="editOrg.support_phone"
                        label="Support Phone"
                        :rules="rules.phoneNumber"
                        browser-autocomplete="off"
                      />
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-form>
              <br />
              <v-layout row wrap>
                <v-flex sm3 style="text-align:center;">
                  <span>Primary Brand Color:</span>
                  <sketch-picker
                    @input="updatePrimary"
                    :value="editOrg.primary_color || '#0d47a1'"
                    style="margin: auto;"
                  ></sketch-picker>
                </v-flex>
                <v-flex sm3 style="text-align:center;">
                  <span>Secondary Brand Color:</span>
                  <sketch-picker
                    @input="updateSecondary"
                    :value="editOrg.secondary_color || '#ffffff'"
                    style="margin: auto;"
                  ></sketch-picker>
                </v-flex>
                <v-flex sm3 style="text-align:center;">
                  <span>Organization Logo:</span><br />
                  <img
                    :src="'data:image/png;base64,' + editOrg.curr_logo"
                    alt="Organization Logo"
                    style="max-width: 100%;"
                  />
                  <input
                    type="file"
                    accept="image/*"
                    ref="logoFile"
                    v-on:change="handleFileUploads('logo')"
                  />
                  <br />
                  <span
                    v-if="failedLogo"
                    style="color: red;"
                    v-html="invalidFileMessage"
                  >
                  </span>
                </v-flex>
                <v-flex sm3 style="text-align:center;">
                  <span>Organization Icon:</span><br />
                  <img
                    :src="'data:image/png;base64,' + editOrg.curr_icon"
                    alt="Organization Icon"
                    style="max-width: 100%;"
                  />
                  <input
                    type="file"
                    accept="image/*"
                    ref="iconFile"
                    v-on:change="handleFileUploads('icon')"
                  />
                  <br />
                  <span
                    v-if="failedIcon"
                    style="color: red;"
                    v-html="invalidFileMessage"
                  >
                  </span>
                </v-flex>
              </v-layout>
              <v-btn
                :disabled="!fileIsImage || !isOrgSettingValid"
                color="primary"
                @click="save(editOrg)"
                style="float:right;"
              >
                Save
              </v-btn>
            </v-tab-item>
          </v-tabs>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="newOrg"
      transition="dialog-bottom-transition"
      persistent
    >
      <v-card>
        <v-toolbar
        class="primary-color"
        flat
        dark
        >
          Organization Details
        </v-toolbar>
        <v-card-text>
          <v-container>
            <v-layout justify-end>
              <v-flex xs2>
                  <span style="float:right">* - Required</span>
              </v-flex>
            </v-layout>
            <div>
              <v-layout row wrap>
                <v-flex>
                  <v-form ref="orgNameForm" v-model="isOrgValid">
                    <v-text-field
                      v-model="editOrg.name"
                      :rules="rules.orgName"
                      label="Organization Name*"
                      browser-autocomplete="off"
                      required
                    />
                    <v-text-field
                      v-model="editOrg.subdomain"
                      :rules="rules.orgSubDomain"
                      label="Organization Subdomain*"
                      browser-autocomplete="off"
                      :suffix="'.' + getRootDomain()"
                      required
                    />
                  </v-form>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex sm6 :style="{'text-align':'center', 'margin-top' : '2em'}">
                  <span>Primary Brand Color*</span>
                  <sketch-picker
                    @input="updatePrimary"
                    :value="editOrg.primary_color || '#000000'"
                    style="margin: auto;"
                  ></sketch-picker>
                </v-flex>
                <v-flex sm6 :style="{'text-align':'center', 'margin-top' : '2em'}">
                  <span>Secondary Brand Color*</span>
                  <sketch-picker
                    @input="updateSecondary"
                    :value="editOrg.secondary_color || '#ffffff'"
                    style="margin: auto;"
                  ></sketch-picker>
                </v-flex>
                <v-flex sm6 style="text-align:center;">
                  <span><br /><br />Organization Logo:</span><br />
                  <input
                    type="file"
                    ref="logoFile"
                    v-on:change="handleFileUploads('logo')"
                  />
                </v-flex>
                <v-flex sm6 style="text-align:center;">
                  <span><br /><br />Organization Icon:</span><br />
                  <input
                    type="file"
                    ref="iconFile"
                    v-on:change="handleFileUploads('icon')"
                  />
                </v-flex>
              </v-layout>
            </div>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="newOrg = false"
            flat
          >
            Cancel
          </v-btn>
          <v-btn
            @click="newOrg=false;firstUser=true"
            color="primary"
            :disabled="!isOrgButtonClickable"
          >
            Create Organization
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog 
      v-model="firstUser"
      persistent
    >
      <v-card>
        <v-toolbar
        class="primary-color"
        flat
        dark
        >
          Administrative User Creation
        </v-toolbar>
        <v-card-text>
          <v-container>
            <v-layout justify-end>
              <v-flex xs2>
                  <span style="float:right">* - Required</span>
              </v-flex>
            </v-layout>
            <div>
              <h2 style="text-align:center;padding:10px;">
                Create administrative user for {{editOrg.name}}<br />
              </h2>
              <v-form ref="editForm" v-model="isUserValid">
                <v-container fluid>
                  <v-layout
                    row
                    wrap
                  >
                    <v-flex xs6>
                      <v-text-field
                        v-model="newOrgUser.last_name"
                        autofocus
                        label="Last Name *"
                        hide-details
                        :rules="rules.lastName"
                        required
                      />
                    </v-flex>
                    <v-flex xs6>
                      <v-text-field
                        v-model="newOrgUser.first_name"
                        label="First Name *"
                        hide-details
                        :rules="rules.firstName"
                        required
                      />
                    </v-flex>
                  </v-layout>
                  <v-layout row wrap>
                    <v-flex>
                      <v-text-field
                        v-model="newOrgUser.email"
                        :error-messages="errorMessages['email']"
                        :rules="rules.email"
                        :hide-details="!newOrgUser.email"
                        type="email"
                        label="E-mail *"
                        required
                      />
                    </v-flex>
                  </v-layout>
                  <v-layout row wrap>
                    <v-flex>
                      <v-text-field
                        v-model="newOrgUser.username"
                        :error-messages="errorMessages['username']"
                        :rules="rules.username"
                        label="Username *"
                        required
                      />
                    </v-flex>
                  </v-layout>
                  <v-layout row wrap>
                    <v-flex>
                      <v-text-field
                        v-model="newOrgUser.password"
                        :error-messages="errorMessages['password']"
                        :rules="rules.password"
                        type="password"
                        label="Password *"
                        autocomplete="new-password"
                        required
                      />
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-form>
            </div>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="newOrg = true;firstUser = false" 
            flat
          >
            Cancel
          </v-btn>
          <v-btn
            @click="createOrgAndUser()"
            color="primary"
            :disabled="!isUserValid"
          >
            Create User
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from '@/api'
import pagination from '@/components/global/pagination'
import Editor from '@/components/admin/Editor'
import SuperUserUsers from './SuperUserUsers'
import { Sketch } from 'vue-color'

export default {
  name: 'UserList',
  mixins: [
    pagination
  ],
  components: {
    Editor,
    'sketch-picker': Sketch,
    SuperUserUsers
  },
  data () {
    return {
      orgs: [],
      pagination: {},
      searchText: '',
      paginationStrings: [
        10, 25, 50
      ],
      headers: [
        { text: '', value: 'icon', sortable: false, width: 55 },
        { text: 'Organization', value: 'name' },
        { text: 'Subdomain', value: 'subdomain' },
        {
          text: 'Actions', align: 'center', sortable: false
        }
      ],
      loading: true,
      editOrg: {},
      editing: false,
      newLogo: '',
      newIcon: '',
      newOrg: false,
      newOrgStep: 1,
      newOrgUser: {},
      errorMessages: [],
      minChars: 0,
      rules: {
        orgName: [v => (v || '').length > this.minChars || 'Must have an organization name'],
        orgSubDomain: [v =>  /[A-Za-z0-9](?:[A-Za-z0-9\-]{1,61}[A-Za-z0-9])?/.test(v) || "Must have a subdomain"],
        firstName: [v => (v || '').length > this.minChars || "Must enter first name"],
        lastName: [v => (v || '').length > this.minChars || "Must enter last name"],
        email: [v => /.+@.+/.test(v) || 'E-mail must be valid'],
        password: [v => !!v || 'Password is required'],
        username: [v => !!v || 'Username is required'],
        phoneNumber: [v => /^\(?([0-9]{3})\)?[-.●]?([0-9]{3})[-.●]?([0-9]{4})$/.test(v) || "Must have a valid phone number"]
      },
      isOrgValid: false,
      isColorsPicked: false,
      isCreatingOrg: true,
      isOrgLogoPicked: false,
      isOrgIconPicked: false,
      firstUser: false,
      isUserValid: false,
      isOrgSettingValid: false,
      fileIsImage: true,
      failedLogo: false,
      failedIcon: false,
      nonCompatibleImageTypes: [
        'tiff',
        'svg+xml'
      ],
      invalidFileMessage: `File is not compatible <br /> Please upload a PNG, JPEG, or GIF`
    }
  },
  computed: {
    totalUsers () {
      return this.pagination.totalItems
    },
    isOrgButtonClickable () {
      return this.isOrgValid && this.isColorsPicked
    }
  },
  methods: {
    fetch () {
      this.loading = true
      api.users.superOrgs.list({
        search: this.searchText, ordering: (this.pagination.descending ? '-' : '') + this.pagination.sortBy, pageNum: this.pagination.page, count: this.pagination.rowsPerPage
      })
        .then(data => {
          this.orgs = data.results
          this.pagination.page = data.page_number
          this.pagination.totalItems = data.count
          this.loading = false
          this.componentCreated = true
        })
    },
    save (org) {
      const data = new FormData()
      if (org.id) {
        data.append("id", org.id)
      }
      data.append("name", org.name)
      data.append("enabled", org.enabled)
      data.append("subdomain", org.subdomain)
      data.append("primary_color", org.primary_color)
      data.append("secondary_color", org.secondary_color)
      if(org.support_phone){
        data.append("support_phone", org.support_phone)
      }
      if(org.support_email){
        data.append("support_email", org.support_email)
      }
      if (this.newLogo !== '') {
        data.append("logo", this.newLogo[0])
      }
      if (this.newIcon !== '') {
        data.append("icon", this.newIcon[0])
      }
      api.users.superOrgs.save(data)
        .then(organization => {
          this.newLogo = ''
          this.newIcon = ''
          this.editing = false
          if (!org.id) {
            this.orgs.push(organization)
          }
        })
        .catch(() => {
          alert('Something went wrong while saving that organizationObject.')
        })
    },
    updatePrimary (value) {
      this.editOrg.primary_color = value.hex8
      this.updateIsColorPicked()
    },
    updateSecondary (value) {
      this.editOrg.secondary_color = value.hex8
      this.updateIsColorPicked()  
    },
    handleFileUploads (location) {
      if (location === 'icon') {
        this.fileIsImage = this.isFileAnImage(this.$refs.iconFile.files[0])
        if(this.fileIsImage){
          this.newIcon = this.$refs.iconFile.files
          this.failedIcon = false
        } else {
          this.failedIcon = true
        }
      } else {
        this.fileIsImage = this.isFileAnImage(this.$refs.logoFile.files[0])
        if(this.fileIsImage){
          this.newLogo = this.$refs.logoFile.files
          this.failedLogo = false
        } else {
          this.failedLogo = true
        }
      }
    },
    search () {
      this.pagination.page = 1
      var queryParam = {}
      if (this.searchText) {queryParam['search'] = this.searchText}
      this.$router.push({ query: queryParam})
    },
    createOrgAndUser () {
      const data = new FormData()
      data.append("name", this.editOrg.name)
      data.append("subdomain", this.editOrg.subdomain)
      data.append("primary_color", this.editOrg.primary_color)
      data.append("secondary_color", this.editOrg.secondary_color)
      if (this.newLogo !== '') {
        data.append("logo", this.newLogo[0])
      }
      if (this.newIcon !== '') {
        data.append("icon", this.newIcon[0])
      }
      api.users.superOrgs.save(data)
        .then(organization => {
          this.newLogo = ''
          this.newIcon = ''
          this.orgs.push(organization)
          this.editOrg = organization
          this.saving = true
          this.newOrgUser.org = this.editOrg.id
          this.newOrgUser.is_staff = 1
          this.newOrgUser.is_org_admin = 1
          api.users.superUser.save(this.newOrgUser, [400])
            .then(user => {
              this.firstUser = false
              this.newOrgUser = {}
            })
            .catch(error => {
              if (error.response.status === 400) {
                this.errorMessages = JSON.parse(error.request.response)
              }
            })
        })
       .catch(() => {
          alert('Something went wrong while saving that organizationObject.')
        })
    },
    updateIsColorPicked () {
      if(this.editOrg.hasOwnProperty("primary_color") && this.editOrg.hasOwnProperty("secondary_color")){
        this.isColorsPicked = true
      }
    },
    isFileAnImage (file) {
      return file.type.split('/')[0] === 'image' && !this.nonCompatibleImageTypes.includes(file.type.split('/')[1]) 
    }
  },
  created () {
    this.documentTitle('Organization Management')
  }
}
</script>

<style>

.disabled-user {
  background: #FFCDD2;
}

.search-bar {
  margin-top: 1em;
}

table {
  margin-top: 1em;
}

</style>
